import React, { useState, useEffect } from 'react';
import { InputFieldState, ApplicantState, AgencyState, RightHolderState, TempSavedValue, FormStateWithEnteredValues } from '../types';
import { PdfCreation, isError } from './utils';
import InstructionsForUsing from '../InstructionsForUsing';
import ImportingDocumentsForm from '../ImportingDocumentsForm';
import EnteringDocumentInformationForm from '../EnteringDocumentInformationForm';
import documentTypeOptions from '../DocumentTypeForm/options.json';
import paymentOptions from '../PaymentForm/options.json';

const pdfMake = window.pdfMake;
const fonts = {
  IPA_mincho: {
    normal: "ipam.ttf",
    bold: "ipam.ttf",
    italics: "ipam.ttf",
    bolditalics: "ipam.ttf",
  },
};

pdfMake.fonts = fonts;

/**
 * システム実行を取得する関するです。
 * @returns {string} - システム実行日の文字列
 */
const getToday = (): string => {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = date.getMonth() + 1;
  const dd = date.getDate();
  return `${yyyy}-${mm.toString().padStart(2, '0')}-${dd.toString().padStart(2, '0')}`;
}

/**
 * PDF作成フォームのコンポーネントです。
 * @returns {JSX.Element} - 委任事項（メイン）フォームのJSX要素
 */
const PdfGeneratorForm: React.FC = () => {

  // フォームで入力された値を保持するためのステートの宣言
  const [documentName, setDocumentName] = useState<InputFieldState>({
    value: documentTypeOptions[0].value,
    errorMessage: ''
  });
  const [submissionDate, setSubmissionDate] = useState<InputFieldState>({ value: getToday(), errorMessage: '' });
  const [jurisdiction, setJurisdiction] = useState<InputFieldState>({ value: '特許', errorMessage: ''});
  const [registrationNumbers, setRegistrationNumbers] = useState<InputFieldState[]>([{ value: '', errorMessage: ''}]);
  const [applicant, setApplicant] = useState<ApplicantState>({
    identificationNumber: {
      value: '',
      errorMessage: ''
    },
    address: {
      value: '',
      errorMessage: ''
    },
    name: {
      value: '',
      errorMessage: ''
    },
    representative: {
      value: '',
      errorMessage: ''
    },
    phoneNumber: {
      value: '',
      errorMessage: ''
    },
    payment: {
      method: {
        value: paymentOptions[0].value,
        errorMessage: ''
      },
      paymentNumber: {
        value: '',
        errorMessage: ''
      }
    }
  });
  const [agency, setAgency] = useState<AgencyState>({
    identificationNumber: {
      value: '',
      errorMessage: ''
    },
    address: {
      value: '',
      errorMessage: ''
    },
    name: {
      value: '',
      errorMessage: ''
    },
    representative: {
      value: '',
      errorMessage: ''
    },
    phoneNumber: {
      value: '',
      errorMessage: ''
    }
  });
  const [isAgencyOpened, setIsAgencyOpened] = useState<boolean>(false);
  const [rightHolders, setRightHolders] = useState<RightHolderState[]>(
    [
      {
        identificationNumber: {
          value: '',
          errorMessage: ''
        },
        address: {
          value: '',
          errorMessage: ''
        },
        name: {
          value: '',
          errorMessage: ''
        }
      }
    ]
  );
  const [specialNotesOnFee, setSpecialNotesOnFee] = useState<{parentItem: InputFieldState, childItem: InputFieldState}>({
    parentItem: { value: '', errorMessage: ''},
    childItem: { value: '', errorMessage: ''}
  });
  const [other, setOther] = useState<InputFieldState>({ value: '', errorMessage: ''});
  const [isSubmitDiabled, setIsSubmitDisabled] = useState<boolean>(true);

  const handleDocumentType = (docmentTypeName: InputFieldState) => {
    setDocumentName(docmentTypeName);
    if(docmentTypeName.value === '自動納付取下書') {
      setSpecialNotesOnFee({
        parentItem: { value: '', errorMessage: ''},
        childItem: { value: '', errorMessage: ''}
      });
    }
  }

  useEffect(() => {
    const isDisabled = isError({
      documentName: documentName,
      submissionDate: submissionDate,
      jurisdiction: jurisdiction,
      registrationNumbers: registrationNumbers,
      applicant: applicant,
      agency: agency,
      rightHolders: rightHolders,
      specialNotesOnFee: specialNotesOnFee,
      other: other
    });
    setIsSubmitDisabled(isDisabled);
  }, [documentName, submissionDate, jurisdiction, registrationNumbers, applicant, agency, rightHolders, specialNotesOnFee, other]);

  /**
   * フォームをリストアする関数です。
   * @param {TempSavedValue} jsonObject - アップロードされた一次保存データ
   * @returns {void}
   */
  const restoreForm = (jsonObject: TempSavedValue) => {
    setDocumentName({
      value: jsonObject['書類名'],
      errorMessage: ''
    });
    setSubmissionDate({
      value: jsonObject['提出日'],
      errorMessage: ''
    });
    setJurisdiction({
      value: jsonObject['法域'],
      errorMessage: ''
    });
    setRegistrationNumbers(jsonObject['登録番号'].map((registrationNumber) => {
      return { value: registrationNumber, errorMessage: '' };
    }));
    setApplicant({
      identificationNumber: {
        value: jsonObject['申出人']['識別番号'],
        errorMessage: ''
      },
      address: {
        value: jsonObject['申出人']['住所又は居所'],
        errorMessage: ''
      },
      name: {
        value: jsonObject['申出人']['氏名又は名称'],
        errorMessage: ''
      },
      representative: {
        value: jsonObject['申出人']['代表者'],
        errorMessage: ''
      },
      phoneNumber: {
        value: jsonObject['申出人']['電話番号'],
        errorMessage: ''
      },
      payment: {
        method: {
          value: jsonObject['申出人']['納付方法']['納付種別'],
          errorMessage: ''
        },
        paymentNumber: {
          value: jsonObject['申出人']['納付方法']['納付番号'],
          errorMessage: ''
        }
      }
    });
    setAgency({
      identificationNumber: {
        value: jsonObject['代理人']['識別番号'],
        errorMessage: ''
      },
      address: {
        value: jsonObject['代理人']['住所又は居所'],
        errorMessage: ''
      },
      name: {
        value: jsonObject['代理人']['氏名又は名称'],
        errorMessage: ''
      },
      representative: {
        value: jsonObject['代理人']['代表者'],
        errorMessage: ''
      },
      phoneNumber: {
        value: jsonObject['代理人']['電話番号'],
        errorMessage: ''
      }
    });
    setRightHolders(jsonObject['権利者'].map((rightHolder) => {
      return ({
        identificationNumber: {
          value: rightHolder['識別番号'],
          errorMessage: ''
        },
        address: {
          value: rightHolder['住所又は居所'],
          errorMessage: ''
        },
        name: {
          value: rightHolder['氏名又は名称'],
          errorMessage: ''
        }
      });
    }));
    setSpecialNotesOnFee({
      parentItem: { value: jsonObject['特許料等に関する特記事項']['親項目'], errorMessage: ''},
      childItem: { value: jsonObject['特許料等に関する特記事項']['子項目'], errorMessage: ''}
    });
    setOther({
      value: jsonObject['その他'],
      errorMessage: ''
    });

    if(jsonObject['代理人']['識別番号'] === '' && jsonObject['代理人']['住所又は居所'] === '' && jsonObject['代理人']['氏名又は名称'] === '' && jsonObject['代理人']['代表者'] === '' && jsonObject['代理人']['電話番号'] === '') {
      setIsAgencyOpened(false);
    } else {
      setIsAgencyOpened(true);
    }
  }

  /**
   * フォームをリセットする関数です。
   * @returns {void}
   */
  const resetForm = (): void => {
    const confirm: boolean = window.confirm('入力内容をリセットしますか？');
    if(confirm === true) {
      setDocumentName({
        value: '自動納付申出書',
        errorMessage: ''
      });
      setSubmissionDate({
        value: getToday(),
        errorMessage: ''
      });
      setJurisdiction({
        value: '特許',
        errorMessage: ''
      });
      setRegistrationNumbers([{
        value: '',
        errorMessage: ''
      }]);
      setApplicant({
        identificationNumber: {
          value: '',
          errorMessage: ''
        },
        address: {
          value: '',
          errorMessage: ''
        },
        name: {
          value: '',
          errorMessage: ''
        },
        representative: {
          value: '',
          errorMessage: ''
        },
        phoneNumber: {
          value: '',
          errorMessage: ''
        },
        payment: {
          method: {
            value: '口座振替',
            errorMessage: ''
          },
          paymentNumber: {
            value: '',
            errorMessage: ''
          }
        }
      });
      setAgency({
        identificationNumber: {
          value: '',
          errorMessage: ''
        },
        address: {
          value: '',
          errorMessage: ''
        },
        name: {
          value: '',
          errorMessage: ''
        },
        representative: {
          value: '',
          errorMessage: ''
        },
        phoneNumber: {
          value: '',
          errorMessage: ''
        }
      });
      setRightHolders(
        [
          {
            identificationNumber: {
              value: '',
              errorMessage: ''
            },
            address: {
              value: '',
              errorMessage: ''
            },
            name: {
              value: '',
              errorMessage: ''
            }
          }
        ]
      );
      setSpecialNotesOnFee({
        parentItem: { value: '', errorMessage: ''},
        childItem: { value: '', errorMessage: ''}
      });
      setOther({ value: '', errorMessage: '' });

      setIsAgencyOpened(false);
    }
  }

  /**
   * 一次保存するための関数です。
   * @returns {void}
   */
  const saveForm = () => {
    const tempFormValue: TempSavedValue = {
      '書類名': documentName.value,
      '提出日': submissionDate.value,
      '法域': jurisdiction.value,
      '登録番号': registrationNumbers.map((registrationNumber) => {
        return registrationNumber.value;
      }),
      '申出人': {
        '識別番号': applicant.identificationNumber.value,
        '住所又は居所': applicant.address.value,
        '氏名又は名称': applicant.name.value,
        '代表者': applicant.representative.value,
        '電話番号': applicant.phoneNumber.value,
        '納付方法': {
          '納付種別': applicant.payment.method.value,
          '納付番号': applicant.payment.paymentNumber.value
        }
      },
      '代理人': {
        '識別番号': agency.identificationNumber.value,
        '住所又は居所': agency.address.value,
        '氏名又は名称': agency.name.value,
        '代表者': agency.representative.value,
        '電話番号': agency.phoneNumber.value
      },
      '権利者': rightHolders.map((rightHolder) => {
        return ({
          '識別番号': rightHolder.identificationNumber.value,
          '住所又は居所': rightHolder.address.value,
          '氏名又は名称': rightHolder.name.value
        });
      }),
      '特許料等に関する特記事項': {
        '親項目': specialNotesOnFee.parentItem.value,
        '子項目': specialNotesOnFee.childItem.value
      },
      'その他': other.value
    };

    const blob = new Blob([JSON.stringify(tempFormValue)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
  
    const link = document.createElement('a');
    link.href = url;
    link.download = `${documentName.value}（特殊申請書類作成支援ツール）_${getToday().replace(/-/g, '')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * 書類PDFを作成する関数です。
   * @returns {void}
   */
  const createPdf = () => {

    const formValue: FormStateWithEnteredValues = {
      documentName: documentName,
      submissionDate: submissionDate,
      jurisdiction: jurisdiction,
      registrationNumbers: registrationNumbers,
      applicant: applicant,
      agency: agency,
      rightHolders: rightHolders,
      specialNotesOnFee: specialNotesOnFee,
      other: other
    }

    const pdfCreation = new PdfCreation(formValue);
    if(pdfCreation.isError()) {
      window.alert('入力内容に不備があります。');
      return;
    }

    const documentDefinition = pdfCreation.getFormValue();
  
    const pdf = pdfMake.createPdf(documentDefinition);
    pdf.download(`${formValue.documentName.value}（特殊申請書類作成支援ツール）_${formValue.jurisdiction.value}第${formValue.registrationNumbers[0].value}${formValue.registrationNumbers.length > 1 ? '等' : ''}_${getToday().replace(/-/g, '')}.pdf`);
  }

  return (
    <>
      <InstructionsForUsing />
      <hr />
      <ImportingDocumentsForm
        restoreForm={restoreForm}
        resetForm={resetForm}
      />
      <hr />
      <EnteringDocumentInformationForm
        documentName={documentName}
        setDocumentName={handleDocumentType}
        submissionDate={submissionDate}
        setSubmissionDate={setSubmissionDate}
        jurisdiction={jurisdiction}
        setJurisdiction={setJurisdiction}
        registrationNumbers={registrationNumbers}
        setRegistrationNumbers={setRegistrationNumbers}
        applicant={applicant}
        setApplicant={setApplicant}
        agency={agency}
        setAgency={setAgency}
        isAgencyOpened={isAgencyOpened}
        rightHolders={rightHolders}
        setRightHolders={setRightHolders}
        specialNotesOnFee={specialNotesOnFee}
        setSpecialNotesOnFee={setSpecialNotesOnFee}
        other={other}
        setOther={setOther}
        saveForm={saveForm}
        resetForm={resetForm}
        isSubmitDiabled={isSubmitDiabled}
        createPdf={createPdf}
      />
    </>
  );
}

export default PdfGeneratorForm;